import type {
  IrrigationPart,
  IrrigationRepairRow,
  IrrigationScheduleBlock,
} from '../dto/proposal-document.dto.js'
import { formatMoneyValue, parseMoney } from './money.js'
import { QUANTITY_PATTERN } from './quantity.js'

/**
 * Pricing an irrigation repair bid.
 *
 * Housecall Pro is the price book (see "Pricing" in docs/irrigation.md): parts
 * at HCP's unit prices, labor as minutes at the labor service for the client's
 * type, and one drive charge per site for its town. Shared, like tree
 * pricing, because the client watches the number change as they choose and
 * the API recomputes it when they accept; two implementations would be two
 * answers to what was agreed.
 *
 * Exact arithmetic, rounded once per site. A part's quantity carries
 * hundredths ("1.5" sprinklers of pipe) and labor is minutes against an hourly
 * price, so the common exact unit is 1/6000 of a cent: hundredths of a
 * quantity times cents, times 60; or minutes times cents an hour, times 100.
 * Rounding each repair and summing would round thirty times and land near the
 * answer; this rounds the site's total once, which is the figure a board
 * checks by hand.
 *
 * Tax follows each item's HCP taxable flag rather than one rate on the whole
 * total as trees do. HCP taxes commercial irrigation labor and not
 * residential, and nearly every part, so a single rate would make the bid and
 * the HCP invoice disagree about what the client owes. A site therefore
 * reports how much of its total is taxable, and the proposal taxes that.
 */

/** 60 for minutes against an hourly price, 100 for hundredths of a quantity. */
const EXACT_PER_CENT = 6000

/**
 * How the office bills irrigation labor: one hour minimum for the job, then
 * quarter hours (the operator, 2026-10-08; every labor line on 150 of its HCP
 * jobs reads that way). Each repair keeps its own minutes; the site's total
 * is what rounds, so the bid's labor is one line HCP can bill exactly:
 * billed hours at the hourly rate.
 */
export const IRRIGATION_LABOR_BILLING = {
  minimumMinutes: 60,
  incrementMinutes: 15,
} as const

/**
 * A part on a repair, as frozen from the copy of the price book.
 *
 * The price book also freezes the Housecall Pro material id. Pricing does
 * not read it, so it is left off; the fields it does read are the part's.
 */
export type PricedPart = Pick<
  IrrigationPart,
  'name' | 'quantity' | 'unitPrice' | 'taxable' | 'gone'
>

/** A repair as pricing sees it: its minutes and its parts. */
export type PricedRepair = Pick<IrrigationRepairRow, 'laborMinutes'> & {
  parts: readonly PricedPart[]
}

/**
 * What one site's repairs are priced by. The card the proposal document
 * freezes, so a field added there is a field this price has to know about.
 */
export type IrrigationRateCard = IrrigationScheduleBlock['rateCard']

/** Why a repair cannot be priced, which stops a proposal being sent. */
export const UNPRICEABLE_REASONS = [
  'noWork',
  'noLabor',
  'partGone',
  'badPrice',
] as const

export type UnpriceableReason = (typeof UNPRICEABLE_REASONS)[number]

export const UNPRICEABLE_REASON_MESSAGES: Record<UnpriceableReason, string> = {
  noWork: 'has no labor or parts recorded',
  noLabor: 'has labor, but no labor service is chosen for this client type',
  partGone: 'uses a part that is no longer in Housecall Pro',
  badPrice: 'has a part whose price or quantity cannot be read',
}

/** A quantity in hundredths, or null when it is not one. */
export function quantityHundredths(value: string): number | null {
  const trimmed = value.trim()
  if (!QUANTITY_PATTERN.test(trimmed)) return null
  const [whole = '0', fraction = ''] = trimmed.split('.')
  return Number(whole) * 100 + Number(fraction.padEnd(2, '0'))
}

/**
 * Why this repair cannot be priced with this card, or null when it can.
 *
 * The first reason found: a repair with nothing recorded is that before it is
 * anything else, and a part that will not read is worth one message rather
 * than several.
 */
export function unpriceableReason(
  repair: PricedRepair,
  card: IrrigationRateCard,
): UnpriceableReason | null {
  const minutes = repair.laborMinutes ?? 0
  if (minutes <= 0 && repair.parts.length === 0) return 'noWork'
  if (minutes > 0 && card.labor === null) return 'noLabor'
  if (
    minutes > 0 &&
    card.labor !== null &&
    parseMoney(card.labor.pricePerHour) === null
  ) {
    return 'badPrice'
  }
  for (const part of repair.parts) {
    if (part.gone) return 'partGone'
    if (parseMoney(part.unitPrice) === null) return 'badPrice'
    if (quantityHundredths(part.quantity) === null) return 'badPrice'
  }
  return null
}

/** What one site's included repairs come to. */
export interface IrrigationSiteAmount {
  /** Everything, before tax, rounded once. Exact decimal string. */
  amount: string
  /** The part of `amount` HCP marks taxable, rounded once. */
  taxable: string
  /** Labor across the included repairs, as recorded. */
  laborMinutes: number
  /** What that labor is billed as: rounded up, never under the minimum. */
  billedMinutes: number
  /**
   * The drive charge, or null when none applies: no town matched, or no
   * work is left at the site. It exists to get a technician there.
   */
  driveCharge: string | null
}

/**
 * Price the repairs included at one site.
 *
 * `repairs` is what the client has kept; leaving everything out drops the
 * drive charge too. A repair that cannot be priced counts as nothing here:
 * sending is refused before that matters (see `unpriceableReason`), and a
 * draft should show what can be priced rather than nothing at all.
 */
export function priceIrrigationSite(
  repairs: readonly PricedRepair[],
  card: IrrigationRateCard,
): IrrigationSiteAmount {
  let exact = 0
  let taxableExact = 0
  let laborMinutes = 0
  let included = 0

  const laborCents =
    card.labor === null ? null : parseMoney(card.labor.pricePerHour)

  for (const repair of repairs) {
    if (unpriceableReason(repair, card) !== null) continue
    included += 1
    const minutes = repair.laborMinutes ?? 0
    if (minutes > 0 && laborCents !== null) laborMinutes += minutes
    for (const part of repair.parts) {
      const hundredths = quantityHundredths(part.quantity)
      const unitCents = parseMoney(part.unitPrice)
      if (hundredths === null || unitCents === null) continue
      const value = hundredths * unitCents * 60
      exact += value
      if (part.taxable) taxableExact += value
    }
  }

  // Labor is billed on the site's total, not repair by repair: the minimum
  // and the rounding are the visit's, as the drive charge is.
  const billedMinutes =
    card.labor === null ? 0 : billedLaborMinutes(laborMinutes, card.labor)
  if (billedMinutes > 0 && laborCents !== null && card.labor !== null) {
    const value = billedMinutes * laborCents * 100
    exact += value
    if (card.labor.taxable) taxableExact += value
  }

  let driveCharge: string | null = null
  if (included > 0 && card.driveCharge !== null) {
    const driveCents = parseMoney(card.driveCharge.price)
    if (driveCents !== null) {
      driveCharge = formatMoneyValue(driveCents)
      exact += driveCents * EXACT_PER_CENT
      if (card.driveCharge.taxable) taxableExact += driveCents * EXACT_PER_CENT
    }
  }

  return {
    amount: formatMoneyValue(Math.round(exact / EXACT_PER_CENT)),
    taxable: formatMoneyValue(Math.round(taxableExact / EXACT_PER_CENT)),
    laborMinutes,
    billedMinutes,
    driveCharge,
  }
}

/**
 * Labor as billed: the total rounded up to the next increment, never under
 * the minimum, and nothing at all when no labor was recorded. A document sent
 * before the rule was frozen into it carries a minimum of 0 and an increment
 * of 1, which bills the minutes as they are.
 */
export function billedLaborMinutes(
  minutes: number,
  labor: { minimumMinutes: number; incrementMinutes: number },
): number {
  if (minutes <= 0) return 0
  const increment = Math.max(1, labor.incrementMinutes)
  const rounded = Math.ceil(minutes / increment) * increment
  return Math.max(labor.minimumMinutes, rounded)
}
